import apiClient, { PaginatedResponse } from '@/ApiClient/ApiClient'
import { apiService } from '@/ApiClient/ApiService'
import { createDataTableService } from '@/ApiClient/DataTableService'
import { defaultPageSize } from '@/services/services-config'
import { Permission, Role, UserType } from '@/types/interfaces'
import { omit } from 'lodash-es'

export const UserServices = {
  getUser: async (userId: string) => {
    const response = await apiClient.get<UserType>(`/users/${userId}`)
    return response
  },
  getUsers: createDataTableService<UserType>({
    endpoint: '/users',
    extraParams: { includeRoles: true, includeDepartments: true },
    paramPreprocessor: params => ({
      ...params,
      includeRoles: params.includeRoles ?? true,
      includeDepartments: params.includeDepartments ?? true,
    }),
  }),
  updateUser: async (userId: string, userData: Partial<UserType>) => {
    const userToUpdate = omit(userData, 'createdAt', 'updatedAt', 'new_password')
    const response = await apiClient.put<UserType>(`/users/${userId}`, userToUpdate)
    return response
  },
  updateUserActive: async (userId: string, active: boolean) => {
    const response = await apiClient.patch<UserType>(`/users/${userId}/active`, { active })
    return response
  },
  updateOwnProfile: async (userId: string, userData: Partial<UserType>) => {
    const response = await apiClient.put<UserType>(`/users/me/${userId}`, userData)
    return response
  },
  profile: async () => {
    const response = await apiClient.get<UserType>('/auth/profile')
    return response
  },
  refreshToken: async () => {
    const response = await apiClient.post<UserType>('/auth/refresh')
    return response
  },
  getRoles: async (params?: {
    includePermissions?: boolean
    pageIndex?: number
    pageSize?: number
  }): Promise<PaginatedResponse<Role>> => {
    console.log('🚀 ~ getRoles ~ params:', params)
    const response = await apiService.fetchPaginated<Role>('/roles', {
      params: {
        order: 'ASC',
        page: params?.pageIndex || 1,
        take: params?.pageSize || defaultPageSize,
        includePermissions: params?.includePermissions || true,
      },
    })
    return response
  },
  getPermissions: async (params?: { pageIndex?: number; pageSize?: number }) => {
    const response = await apiService.fetchPaginated<Permission>('/permissions', {
      params: {
        order: 'ASC',
        page: params?.pageIndex || 1,
        take: params?.pageSize || defaultPageSize,
      },
    })
    return response
  },
  createRole: async (roleData: Partial<Role>) => {
    try {
      const response = await apiClient.post<Role>('/roles', roleData)
      return response
    } catch (error) {
      throw error
    }
  },
  updateRole: async (roleId: string, roleData: Partial<Role>) => {
    try {
      const response = await apiClient.put(`/roles/${roleId}`, roleData)
      return response
    } catch (error) {
      throw error
    }
  },
  deleteRole: async (roleId: string) => {
    try {
      const response = await apiClient.delete(`/roles/${roleId}`)
      return response
    } catch (error) {
      throw error
    }
  },
  createPermission: async (permissionData: Partial<Permission>) => {
    try {
      const response = await apiClient.post<Permission>('/permissions', permissionData)
      return response
    } catch (error) {
      throw error
    }
  },
  updatePermission: async (permissionId: string, permissionData: Partial<Permission>) => {
    try {
      const response = await apiClient.put(`/permissions/${permissionId}`, permissionData)
      return response
    } catch (error) {
      throw error
    }
  },
  deletePermission: async (permissionId: string) => {
    try {
      const response = await apiClient.delete(`/permissions/${permissionId}`)
      return response
    } catch (error) {
      throw error
    }
  },
  logOut: async () => {
    try {
      const response = await apiClient.post('/users/logout')
      return response
    } catch (error) {
      console.log(error)
    }
  },
  forgotPassword: async (email: string) => {
    const response = await apiClient.post('/auth/forgot-password', { email })
    return response
  },
  requestActivation: async (email: string) => {
    const response = await apiClient.post('/auth/request-activation', { email })
    return response
  },
  resetPassword: async (token: string, newPassword: string) => {
    const response = await apiClient.post('/auth/reset-password', { token, newPassword })
    return response
  },
}
